import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { Edit2, Trash2 } from 'lucide-react';
import { listUsers, createUser, updateUser, deactivateUser } from '../../services/adminService';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton';
import '../../styles/UserManagement.css';

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const { register, handleSubmit, reset, setValue, formState: { errors, isSubmitting } } = useForm();

  const fetchUsers = useCallback(async () => {
    try {
      const res = await listUsers();
      if (res.success) setUsers(res.data);
    } catch { setError('Erreur lors du chargement'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const onSubmit = async (data) => {
    setError('');
    setSuccess('');
    try {
      if (editingId) {
        const payload = { name: data.name, email: data.email, role: data.role };
        if (data.password) payload.password = data.password;
        await updateUser(editingId, payload);
        setSuccess('Utilisateur mis à jour');
      } else {
        await createUser({ name: data.name, email: data.email, password: data.password, role: data.role });
        setSuccess('Utilisateur créé');
      }
      reset();
      setShowForm(false);
      setEditingId(null);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Erreur');
    }
  };

  const handleEdit = (user) => {
    setEditingId(user.id);
    setValue('name', user.name);
    setValue('email', user.email);
    setValue('role', user.role);
    setValue('password', '');
    setShowForm(true);
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm('Désactiver ce compte ?')) return;
    try {
      await deactivateUser(id);
      setSuccess('Compte désactivé');
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Erreur');
    }
  };

  if (loading) return <LoadingSkeleton lines={6} />;

  return (
    <div className="user-management-page">
      <div className="toolbar header-actions" style={{ justifyContent: 'flex-end' }}>
        <button className="btn btn-primary" onClick={() => { setShowForm(!showForm); setEditingId(null); reset(); }}>
          {showForm ? 'Annuler' : '+ Nouveau compte'}
        </button>
      </div>

      {error && <div className="error-banner">{error}</div>}
      {success && <div className="success-banner">{success}</div>}

      {showForm && (
        <form onSubmit={handleSubmit(onSubmit)} className="user-form-card">
          <h3>{editingId ? 'Modifier le compte' : 'Nouveau compte'}</h3>

          <div className="form-row">
            <div className="form-group">
              <label>Nom complet *</label>
              <input {...register('name', { required: 'Requis', minLength: { value: 2, message: 'Min 2 caractères' } })} placeholder="Prénom Nom" />
              {errors.name && <span className="field-error">{errors.name.message}</span>}
            </div>
            <div className="form-group">
              <label>Email *</label>
              <input type="email" {...register('email', { required: 'Requis', pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Email invalide' } })} placeholder="prenom@exemple.com" />
              {errors.email && <span className="field-error">{errors.email.message}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group" style={{ flex: '0 0 auto', minWidth: '180px' }}>
              <label>Rôle *</label>
              <select {...register('role', { required: 'Requis' })}>
                <option value="">— Sélectionnez —</option>
                <option value="security">Agent de sécurité</option>
                <option value="staff">Personnel</option>
                <option value="admin">Administrateur</option>
                <option value="entreprise">Entreprise</option>
              </select>
              {errors.role && <span className="field-error">{errors.role.message}</span>}
            </div>
          </div>

          <div className="form-group">
            <label>{editingId ? 'Nouveau mot de passe (laisser vide pour ne pas changer)' : 'Mot de passe *'}</label>
            <input
              type="password"
              {...register('password', editingId ? {} : { required: 'Requis', minLength: { value: 8, message: 'Min 8 caractères' } })}
            />
            {errors.password && <span className="field-error">{errors.password.message}</span>}
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'En cours...' : editingId ? 'Mettre à jour' : 'Créer'}
            </button>
          </div>
        </form>
      )}

      {!showForm && (
        <div className="table-responsive">
          <table className="data-table">
            <thead>
              <tr>
                <th>Nom</th>
                <th>Email</th>
                <th>Rôle</th>
                <th>Créé le</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr 
                  key={u.id} 
                  className={`${!u.is_active ? 'row-inactive' : ''} ${expandedId === u.id ? 'expanded' : ''}`}
                  onClick={() => setExpandedId(expandedId === u.id ? null : u.id)}
                >
                  <td data-label="Nom">{u.name}</td>
                  <td data-label="Email">{u.email}</td>
                  <td data-label="Rôle"><span className={`badge badge-${u.role}`}>{u.role}</span></td>
                  <td data-label="Créé le">{new Date(u.created_at).toLocaleDateString('fr-FR')}</td>
                  <td data-label="Statut">{u.is_active ? 'Actif' : 'Désactivé'}</td>
                  <td data-label="Actions" className="actions-cell" onClick={(e) => e.stopPropagation()}>
                    {u.is_active && (
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                        <button className="btn btn-sm btn-secondary btn-icon" onClick={(e) => { e.stopPropagation(); handleEdit(u); }} title="Modifier">
                          <Edit2 size={14} />
                        </button>
                        <button className="btn btn-sm btn-danger btn-icon" onClick={(e) => { e.stopPropagation(); handleDeactivate(u.id); }} title="Désactiver">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
